/**
 * Prompt-Based DD Assistant (MVP Implementation)
 *
 * Uses direct LLM calls with startup context for due diligence analysis.
 * This is the simpler, session-only implementation without RAG or persistent memory.
 */

import { sendDDChatMessage } from '@/lib/supabase/edge-functions';
import type {
  IDDAssistant,
  DDAssistantContext,
  DDAssistantResponse,
  DDAssistantMode,
  DDConversationSummary,
} from './assistant.types';

/**
 * MVP implementation of DD Assistant using prompt-based approach.
 *
 * Characteristics:
 * - Session-only: no persistent conversation history
 * - Uses startup profile + score context (no document RAG)
 * - Direct LLM calls via edge function
 * - Lower cost, faster response
 */
export class PromptBasedAssistant implements IDDAssistant {
  private readonly mode: DDAssistantMode = 'mvp';

  /**
   * Send a message and get a response.
   */
  async chat(context: DDAssistantContext): Promise<DDAssistantResponse> {
    const startTime = Date.now();

    // Call existing edge function
    const result = await sendDDChatMessage({
      message: context.message,
      history: context.history ?? [],
      startupId: context.startupId, // B2: server rebuilds context + gates on this
      startupContext: context.startupContext, // stripped before server send (SEC-DOC-03)
      investorPreferences: undefined, // Can be extended later
    });

    const processingTimeMs = Date.now() - startTime;

    if (result.error || !result.data?.success || !result.data.data) {
      throw new Error(result.error || result.data?.error || 'Failed to get response');
    }

    const { message, suggestedQuestions, topicsDiscussed } = result.data.data;
    const usage = result.data.usage;

    return {
      message: {
        ...message,
        metadata: {
          ...message.metadata,
          // Mark as MVP mode for UI awareness
        },
      },
      sources: undefined, // MVP doesn't provide rich sources
      suggestedQuestions: suggestedQuestions ?? [],
      topicsDiscussed: topicsDiscussed ?? [],
      usage: {
        inputTokens: usage?.inputTokens ?? 0,
        outputTokens: usage?.outputTokens ?? 0,
        totalTokens: usage?.totalTokens ?? 0,
        retrievalTokens: 0, // No retrieval in MVP
      },
      mode: this.mode,
      processingTimeMs,
    };
  }

  /**
   * Get conversation history.
   * MVP mode has no persistent history, returns empty array.
   */
  async getHistory(
    _investorId: string,
    _startupId: string
  ): Promise<DDConversationSummary[]> {
    // MVP mode doesn't persist conversations
    return [];
  }

  /**
   * Clear conversation history.
   * MVP mode is session-only, so this is a no-op.
   */
  async clearHistory(
    _investorId: string,
    _startupId: string,
    _permanent?: boolean
  ): Promise<void> {
    // MVP mode doesn't persist conversations, nothing to clear
    return;
  }

  /**
   * Get the current mode identifier.
   */
  getMode(): DDAssistantMode {
    return this.mode;
  }
}

/**
 * Factory function to create PromptBasedAssistant.
 * Used by the main factory when MVP mode is selected.
 */
export function createPromptBasedAssistant(): IDDAssistant {
  return new PromptBasedAssistant();
}
